import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { UsageProfile } from '@thive/fddm-types';
import { MenuItem, Select, SelectChangeEvent, Stack } from '@mui/material';

import { NumberInput } from '@common/components/inputs';
import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';

import { UsageProfileOptions } from '../types';

import {
  MAX_END_WEIGHT,
  MAX_RATE,
  MAX_START_WEIGHT,
  MIN_END_WEIGHT,
  MIN_RATE,
  MIN_START_WEIGHT,
} from '../consts';
import { useFlowUpdaters } from '../hooks/useFlowUpdaters';

type Props = {
  flowId: string;
  option: UsageProfileOptions;
  usageProfile: UsageProfile;
};

const isUsageProfileType = (value: string): value is UsageProfile['type'] =>
  value === 'LINEAR' || value === 'EXPONENTIAL';

function UsageProfileComponent({ flowId, option, usageProfile }: Props) {
  const { t } = useTranslation();
  const { onGoodsToPersonsProfileValueChange, onGoodsToPersonsProfileTypeChange } =
    useFlowUpdaters(flowId);

  const handleValueChange = useCallback(
    (key: 'startWeight' | 'endWeight' | 'rate', value: number) => {
      onGoodsToPersonsProfileValueChange(option, key, value);
    },
    [option, onGoodsToPersonsProfileValueChange],
  );

  const handleTypeChange = useCallback(
    (value: UsageProfile['type']) => {
      onGoodsToPersonsProfileTypeChange(option, value);
    },
    [option, onGoodsToPersonsProfileTypeChange],
  );

  const handleStartWeightChange = useCallback(
    (value: number) => handleValueChange('startWeight', value),
    [handleValueChange],
  );
  const handleEndWeightChange = useCallback(
    (value: number) => handleValueChange('endWeight', value),
    [handleValueChange],
  );
  const handleRateChange = useCallback(
    (value: number) => handleValueChange('rate', value),
    [handleValueChange],
  );

  const handleTypeSelectChange = useCallback(
    (event: SelectChangeEvent) => {
      const { value } = event.target;
      if (!isUsageProfileType(value)) return;
      handleTypeChange(value);
    },
    [handleTypeChange],
  );

  return (
    <Stack gap={1}>
      <PropertiesLabel
        labelRatio={50}
        i18nkey='interface:properties.flows.goods_to_persons.fields.type.label'
      >
        <Select value={usageProfile.type} onChange={handleTypeSelectChange}>
          <MenuItem value='LINEAR'>
            {t('interface:properties.flows.goods_to_persons.fields.type.linear')}
          </MenuItem>
          <MenuItem value='EXPONENTIAL'>
            {t('interface:properties.flows.goods_to_persons.fields.type.exponential')}
          </MenuItem>
        </Select>
      </PropertiesLabel>

      {usageProfile.type === 'LINEAR' && usageProfile.usageProfileLinear && (
        <>
          <PropertiesLabel
            labelRatio={70}
            i18nkey='interface:properties.flows.goods_to_persons.fields.start_weight.label'
          >
            <NumberInput
              value={usageProfile.usageProfileLinear.startWeight}
              decimals={2}
              minValue={MIN_START_WEIGHT}
              minValueErrorMsg={t('common:minimum_value', { minValue: MIN_START_WEIGHT })}
              maxValue={MAX_START_WEIGHT}
              maxValueErrorMsg={t('common:maximum_value', { maxValue: MAX_START_WEIGHT })}
              onChange={handleStartWeightChange}
            />
          </PropertiesLabel>

          <PropertiesLabel
            labelRatio={70}
            i18nkey='interface:properties.flows.goods_to_persons.fields.end_weight.label'
          >
            <NumberInput
              value={usageProfile.usageProfileLinear.endWeight}
              decimals={2}
              minValue={MIN_END_WEIGHT}
              minValueErrorMsg={t('common:minimum_value', { minValue: MIN_END_WEIGHT })}
              maxValue={MAX_END_WEIGHT}
              maxValueErrorMsg={t('common:maximum_value', { maxValue: MAX_END_WEIGHT })}
              onChange={handleEndWeightChange}
            />
          </PropertiesLabel>
        </>
      )}
      {usageProfile.type === 'EXPONENTIAL' && usageProfile.usageProfileExponential && (
        <PropertiesLabel
          labelRatio={70}
          i18nkey='interface:properties.flows.goods_to_persons.fields.rate.label'
        >
          <NumberInput
            value={usageProfile.usageProfileExponential.rate}
            decimals={2}
            minValue={MIN_RATE}
            minValueErrorMsg={t('common:minimum_value', { minValue: MIN_RATE })}
            maxValue={MAX_RATE}
            maxValueErrorMsg={t('common:maximum_value', { maxValue: MAX_RATE })}
            onChange={handleRateChange}
          />
        </PropertiesLabel>
      )}
    </Stack>
  );
}

export default memo(UsageProfileComponent);
